<script setup lang="ts">
import { computed, getCurrentInstance, useId, type Component } from 'vue'

import { type WithAttributes, type Labeled, toHtml, fw, ui, type Color } from './attributes'
import { useContext } from './context'
import Token from './Token.vue'
import Gap from './Gap.vue'

// #region props
const {
  uiShadow = true,
  uiInlineSize = ui.inlineSize['208'],
  ...props
} = defineProps<WithAttributes<{
  /**
    * Pass `RouterLink` or use the `to()` helper from `~/routing`
    * @default `<a>` tag
    */
  is?: Component | 'a'

  class: {
    color?: Extract<Color, `${string}-solid`>
  }

  title: string
  category?: true | 'h1' | 'h2' | 'h3' | 'h4' | 'h5'

  tags?: string[]
  image?: string | { src: string, style?: 'withPadding' | 'circle', label?: string }
  icon?: string

  uiShadow?: boolean

  /**
   * Small card: 128
   * Medium card: 208
   * Large card: 288
   */
  uiInlineSize?: '128' | '208' | '288'

  uiSpanAll?: boolean

  /**
  * Only use when the default role of a semantic element is not enough.
  */
  role?: string
} & Labeled>>()
// #endregion props

const id = `title-${useId()}`

const image = typeof props.image === 'string' ? { src: props.image } : props.image

const uiInteractive = computed(
  () => !!getCurrentInstance()?.vnode.props?.onClick || undefined
)

// All nested elements bleed inline
useContext({ uiBleedInline: true })
</script>

<template>
  <div
    v-bind="toHtml<'div'>(props)"
    :class="[
      $style.card,
      category && $style['is-category'],
      props.class,
      fw.shape.card,
      fw.padding.small,
      fw.layout.stack,
      !props.class?.includes('color-') && 'color-default-solid'
    ]"
    :style="uiInteractive && ({ cursor: 'pointer' })"
    :ui-inline-size
    :ui-interactive
    :ui-shadow="uiShadow || undefined"
    :ui-span-all="props.uiSpanAll || undefined"
  >
    <!-- Link -->

    <component
      :is
      v-if="is"
      :class="$style.covering"
      v-bind="{ ...toHtml<'a'>({ ariaLabelledby: id }), ...$attrs }"
      :rel="is === 'a' ? 'noopener noreferrer' : undefined"
    />

    <!-- Image -->

    <div
      v-if="$slots.image"
      :class="$style.image"
      ui-bleed
    >
      <slot
        name="image"
        :circle="() => ({
          class: $style.circle
        })
        "
        :src="image"
      />
    </div>
    <img
      v-else-if="image"
      :src="image.src"
      :alt="image.label || ''"
      :title="image.label"
      :class="[
        $style.image,
        image.style === 'withPadding' && $style['with-padding']
      ]"
      ui-bleed
    >

    <!-- Icon -->

    <Icon
      v-if="props.icon"
      :icon="props.icon"
      :aria-label="`${props.icon.replace(/^bi:/, '')} icon`"
      style="position: absolute; inset: auto; inset-block-start: 16px; inset-inline-end: 16px; z-index: 3;"
    />

    <!-- Title -->
    <component
      :is="typeof category === 'string' ? category : 'h3'"
      :id
      :class="[
        $slots.image || image
          ? fw.font.cardTitle
          : fw.font.titleLarge,
        $style.title
      ]"
      ui-indicate-overflow
    >
      {{ title }}
    </component>

    <!-- context action -->

    <div
      v-if="$slots.context"
      :class="$style.context"
    >
      <slot name="context" />
    </div>

    <!-- Content -->

    <slot name="alert" />

    <slot />

    <span
      v-if="tags"
      class="layout-row"
      ui-indicate-overflow
      ui-nowrap
      style="margin-top: 0px;"
    >
      <Token
        v-for="(tag, index) in tags"
        :key="index"
      >
        #{{ tag }}
      </Token>
    </span>

    <Gap
      v-if="$slots.footer || $slots.action"
      style="flex-grow: 1; min-height: 16px;"
    />

    <!-- Footer and Action -->

    <div
      v-if="$slots.footer"
      :class="[fw.layout.row, $style.footer]"
      ui-bleed
    >
      <slot name="footer" />
    </div>

    <div
      v-if="$slots.action"
      :class="$style.action"
      ui-bleed
      ui-indicate-overflow
      ui-nowrap
    >
      <slot name="action" />
    </div>
    <div
      v-if="$slots.topright"
      :class="$style.topright"
    >
      <slot name="topright" />
    </div>
    <div
      v-if="$slots['on-image']"
      :class="image || $slots.image ? $style['on-image'] : $style.center"
    >
      <slot name="on-image" />
    </div>
  </div>
</template>

<style module>
.card {
  position: relative;
  text-wrap: balance;

  >.covering {
    position: absolute;
    inset: 0;
    z-index: 1;

    &:hover~.title {
      text-decoration: underline;
      text-decoration-thickness: 2px;
      text-underline-offset: 4px;
    }
  }

  :is(button, input, a, select):not(.covering) {
      position: relative;
      z-index: 2;
    }

  >.image {
      anchor-name: --card-image;
    overflow: hidden;
    border-radius: var(--fw-border-radius) var(--fw-border-radius) 0 0;
    object-fit: cover;
    aspect-ratio: 1 / 1;
    position: relative;
    pointer-events: none;
    margin-block-end: 12px;

    &.with-padding {
      border-radius: var(--fw-border-radius);
      padding: var(--bleed-inline-start);
    }

    &:not(.with-padding) {
        padding: 0;
    }

    &:has(>.circle) {
      margin-block-end: 0;
      border-radius: 50%;
      padding: 16px;

      > * {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }
    }
  }

  >.icon {
    position: absolute;
    top: 20px;
    right: 20px;

    font-size: 1rem;

    &:global(.large) {
      font-size: 32px;
      margin: -8px;
    }
  }
  &:has(>.image)>.icon {
    top: var(--fw-card-padding);
    right: var(--fw-card-padding);

    font-size: 1.2rem;
  }

  &:has(>.icon):not(:has(.image))>.title {
    padding-right: 22px;
  }
  &.is-category>.title {
    font-size: 1.75em;
    padding-bottom: .125em;
  }

  >.context {
    position: absolute;
    top: 0;
    right: 0;
  }

  >.tags {
    padding: 0 var(--fw-card-padding);
    margin: 8px  -4px 0px -4px;
  }

  >.content {
    overflow: hidden;
    padding: 0 var(--fw-card-padding);
    /* Consider making all line height, vertical paddings, margins and borders,
    a multiple of a global vertical rhythm so that side-by-side lines coincide */
    line-height: 24px;
  }

  >.footer {
    gap: 4px;
    padding: 0;
    margin-top: 0;
  }

  .topright {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .on-image {
    position: absolute;
    position-anchor: --card-image;

    position-area: block-end inline-end;

    inset: -48px;
  }

  /*TODO(refactor): Remove special cases:*/

  /* If both card and an action control has a special color, draw foreground color line above that action control */
  &:global(:is(.primary, .destructive)) > .action :global(:is(.primary, .destructive)) {
      border-top-color: color-mix(in oklab, var(--color) 50%, transparent) !important;
  }

  >.action {
    display: flex;
    padding: 0;
    background: color-mix(in oklab, var(--fw-bg-color) 80%, var(--fw-gray-500));
    border-bottom-left-radius: var(--fw-border-radius);
    border-bottom-right-radius: var(--fw-border-radius);
    margin-top: 12px;
    z-index: 1;

    >*:not(.with-padding) {
      margin: 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      &:not(:first-child) {
        border-bottom-left-radius: 0;
      }
      &:not(:last-child) {
        border-bottom-right-radius: 0;
      }
    }
  }
}
</style>
